/* CBD HUT — Related products (v2)
   Fixes:
   - Correct slider width on first render (handled in Liquid by proper grid classes)
   - Counter shows PAGE count (1/3) instead of weird slide math (e.g. 4/3)
*/
(() => {
  const SELECTOR = 'product-recommendations[data-cbh-related-products]';

  function getTotalSlides(slider){
    const attr = Number(slider.getAttribute('data-total') || 0);
    if (attr) return attr;
    return slider.querySelectorAll('.slider__slide').length;
  }

  function getStep(slider){
    const first = slider.querySelector('.slider__slide');
    if (!first) return slider.clientWidth;

    const cs = getComputedStyle(slider);
    const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  function getVisibleCount(slider){
    const step = getStep(slider);
    if (!step) return 1;
    const visible = Math.round(slider.clientWidth / step);
    return Math.max(1, visible);
  }

  function init(pr){
    if (!pr) return;

    const slider = pr.querySelector('[data-related-slider]');
    if (!slider) return; // don't mark init if content isn't injected yet

    if (slider.dataset.cbhRpInit === 'true') return;
    slider.dataset.cbhRpInit = 'true';

    const currentEl = pr.querySelector('[data-related-current]');
    const totalEl = pr.querySelector('[data-related-total]');
    const prevBtn = pr.querySelector('[data-related-prev]');
    const nextBtn = pr.querySelector('[data-related-next]');

    const totalSlides = getTotalSlides(slider);

    const computePages = () => {
      const visible = getVisibleCount(slider);
      const pages = Math.max(1, Math.ceil(totalSlides / visible));
      return { visible, pages };
    };

    const setCounter = () => {
      if (!currentEl && !totalEl) return;

      const { visible, pages } = computePages();
      if (totalEl) totalEl.textContent = String(pages);

      const step = getStep(slider);
      const pageStep = step * visible;
      if (!pageStep) return;

      const idx = Math.floor((slider.scrollLeft + pageStep * 0.25) / pageStep) + 1;
      const safe = Math.max(1, Math.min(pages, idx));
      if (currentEl) currentEl.textContent = String(safe);
    };

    // Keep counter accurate on scroll + resize
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setCounter();
      });
    };

    slider.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    // Run once now
    setCounter();

    // Prev/Next click fallback if theme slider isn't available
    const hasThemeSlider = typeof customElements !== 'undefined' && !!customElements.get('slider-component');
    if (!hasThemeSlider){
      const scrollByPage = (dir) => {
        const { visible } = computePages();
        const step = getStep(slider);
        slider.scrollBy({ left: dir * step * visible, behavior: 'smooth' });
      };
      if (prevBtn) prevBtn.addEventListener('click', () => scrollByPage(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => scrollByPage(1));
    }

    // Drag (safe: doesn't start on links/buttons)
    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    const isInteractive = (el) => !!el.closest('a,button,input,select,textarea,label,summary,[data-no-drag]');

    slider.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (isInteractive(e.target)) return;

      isDown = true;
      moved = false;
      startX = e.clientX;
      startScroll = slider.scrollLeft;
      slider.classList.add('is-dragging');

      try { slider.setPointerCapture(e.pointerId); } catch (_) {}
    });

    slider.addEventListener('pointermove', (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 6) moved = true;
      slider.scrollLeft = startScroll - dx;
    });

    const end = (e) => {
      if (!isDown) return;
      isDown = false;
      slider.classList.remove('is-dragging');

      try { slider.releasePointerCapture(e.pointerId); } catch (_) {}

      if (moved && e.pointerType === 'mouse'){
        const cancelClick = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          slider.removeEventListener('click', cancelClick, true);
        };
        slider.addEventListener('click', cancelClick, true);
      }
    };

    slider.addEventListener('pointerup', end);
    slider.addEventListener('pointercancel', end);
  }

  function ensureObserver(pr){
    if (!pr || pr._cbhRpObs) return;
    const obs = new MutationObserver(() => init(pr));
    obs.observe(pr, { childList: true, subtree: true });
    pr._cbhRpObs = obs;
  }

  function boot(scope = document){
    scope.querySelectorAll(SELECTOR).forEach((pr) => {
      init(pr);
      ensureObserver(pr);
    });
  }

  if (document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (e) => boot(e.target || document));

  document.addEventListener('shopify:section:unload', (e) => {
    const scope = e.target || document;
    scope.querySelectorAll?.(SELECTOR).forEach((pr) => {
      if (pr._cbhRpObs){
        pr._cbhRpObs.disconnect();
        pr._cbhRpObs = null;
      }
    });
  });
})();
